/** Vault path helpers. All vault paths are POSIX-style, vault-relative,
 * never starting with "/" or containing "..". */

export function normalizePath(p: string): string {
  const parts: string[] = [];
  for (const seg of p.replace(/\\/g, '/').split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      parts.pop();
      continue;
    }
    parts.push(seg);
  }
  return parts.join('/');
}

export function basename(p: string): string {
  const i = p.lastIndexOf('/');
  return i === -1 ? p : p.slice(i + 1);
}

export function dirname(p: string): string {
  const i = p.lastIndexOf('/');
  return i === -1 ? '' : p.slice(0, i);
}

export function extname(p: string): string {
  const b = basename(p);
  const i = b.lastIndexOf('.');
  return i <= 0 ? '' : b.slice(i).toLowerCase();
}

export function joinPath(...segs: string[]): string {
  return normalizePath(segs.filter(Boolean).join('/'));
}

/** Basename without extension — the note's display title. */
export function titleFromPath(p: string): string {
  const b = basename(p);
  const i = b.lastIndexOf('.');
  return i <= 0 ? b : b.slice(0, i);
}

export const isMarkdownPath = (p: string) => extname(p) === '.md';
export const isCanvasPath = (p: string) => extname(p) === '.canvas';

export function ensureMdExtension(p: string): string {
  return isMarkdownPath(p) ? p : `${p}.md`;
}

const IMAGE_EXTS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.avif', '.bmp']);
const AUDIO_EXTS = new Set(['.mp3', '.wav', '.m4a', '.ogg', '.flac']);
const VIDEO_EXTS = new Set(['.mp4', '.webm', '.mov']);
export const isImagePath = (p: string) => IMAGE_EXTS.has(extname(p));
export const isAudioPath = (p: string) => AUDIO_EXTS.has(extname(p));
export const isVideoPath = (p: string) => VIDEO_EXTS.has(extname(p));

/** Reject unsafe vault paths (absolute, traversal, empty). */
export function isSafeVaultPath(p: string): boolean {
  if (!p || p.startsWith('/') || p.includes('\\')) return false;
  const segs = p.split('/');
  return segs.every((s) => s !== '' && s !== '.' && s !== '..');
}

/** True when `p` is a file inside one of `prefixes` (a vault-relative directory
 * such as "vantage"). Normalizes traversal first, so "vantage/../secrets.md"
 * does NOT match. The prefix directory entry itself is not "under" the prefix.
 * Empty prefixes are ignored so a stray "" never matches the whole vault. */
export function isUnderPrefix(p: string, prefixes: readonly string[]): boolean {
  const norm = normalizePath(p);
  if (!norm) return false;
  for (const raw of prefixes) {
    const prefix = normalizePath(raw);
    if (!prefix) continue;
    if (norm.startsWith(`${prefix}/`)) return true;
  }
  return false;
}
